/** Network: find verified people, send limited connection requests, answer them, and handle co-founder introductions. */
import { api } from '../../core/api.js';
import { ROLE_LABEL } from '../../core/constants.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { ago, debounce } from '../../core/format.js';
import { currentView, reload, update } from '../../app/shell.js';
import { av, chips, empty, ic, pageHead, seg, tag } from '../../ui/components.js';
import { toast } from '../../ui/toast.js';

const TABS = [['discover', 'Discover'], ['requests', 'Requests'], ['connections', 'Connections']];

async function fetchPeople(ctx, ui) {
  return api.get('/network/people', { signal: ctx.signal, query: { q: ui.q.trim(), role: ui.role, limit: 30 } });
}

function personCard(p, left) {
  let action;
  if (p.connection === 'connected') action = html`<span class="sm mu">${ic('check')} Connected</span>`;
  else if (p.connection === 'sent') action = html`<span class="sm mu">Request sent</span>`;
  else if (p.connection === 'received') action = html`<button type="button" class="btn soft sm" data-act="net.tab" data-value="requests">Respond</button>`;
  else action = html`<button type="button" class="btn soft sm" data-act="net.connect" data-id="${p.user_id}" data-name="${p.name}" ${left > 0 ? '' : 'disabled'}>${ic('plus')} Connect</button>`;
  return html`<div class="card"><div class="row nw">${av(p.name)}<div class="f1"><h4 style="margin:0">${p.name}</h4><div class="xs mu">${p.headline || ROLE_LABEL[p.role]}${p.city ? `, ${p.city}` : ''}</div></div></div>
    <div class="mt-s">${chips(p.skills)}</div><div class="row between mt-s">${p.verified ? tag('Verified', 'ok') : tag(ROLE_LABEL[p.role])}${action}</div></div>`;
}

function networkRoute(role) {
  return {
    path: `/${role}/network`,
    roles: [role],
    title: 'Network',
    ui: (ctx) => {
      const wanted = ctx.query.get('tab');
      const known = [...TABS.map(([k]) => k), ...(role === 'startup' ? [] : ['intros'])];
      return { tab: known.includes(wanted) ? wanted : 'discover', q: '', role: '' };
    },
    async load(ctx) {
      const ui = { q: '', role: '' };
      const wantsIntros = role !== 'startup';
      const [people, incoming, outgoing, connections, intros] = await Promise.all([
        fetchPeople(ctx, ui),
        api.get('/network/requests', { signal: ctx.signal, query: { box: 'incoming' } }),
        api.get('/network/requests', { signal: ctx.signal, query: { box: 'outgoing' } }),
        api.get('/network/connections', { signal: ctx.signal }),
        wantsIntros ? api.get('/me/intros', { signal: ctx.signal }) : Promise.resolve({ intros: [] }),
      ]);
      return { people: people.people, quota: people.quota, incoming: incoming.requests, outgoing: outgoing.requests, connections: connections.connections, intros: intros.intros };
    },
    view(d, ui) {
      const tabs = [...TABS.map(([k, label]) => [k, k === 'requests' && d.incoming.length ? `${label} (${d.incoming.length})` : label])];
      if (role !== 'startup') tabs.push(['intros', d.intros.length ? `Introductions (${d.intros.length})` : 'Introductions']);
      let body;
      if (ui.tab === 'discover') {
        body = html`<div class="row mb"><input style="width:260px" placeholder="Search by name" aria-label="Search people" value="${ui.q}" data-input="net.search">
          ${seg([['', 'Everyone'], ['student', 'Students'], ['professional', 'Freelancers']], ui.role, 'net.role', 'Show')}</div>
          ${d.people.length ? html`<div class="grid g3">${d.people.map((p) => personCard(p, d.quota.left))}</div>` : empty('users', 'No one found', 'Try a different name or filter.')}`;
      } else if (ui.tab === 'requests') {
        body = html`<h3>Received</h3>${d.incoming.length
          ? html`<div class="list">${d.incoming.map((r) => html`<div class="li">${av(r.name, 'sm')}<span class="f1"><b>${r.name}</b><br><span class="xs mu">${ROLE_LABEL[r.role]}, ${ago(r.at)}</span></span>
              <button type="button" class="btn sm" data-act="net.respond" data-id="${r.id}" data-accept="true">Accept</button><button type="button" class="btn ghost sm" data-act="net.respond" data-id="${r.id}" data-accept="false">Decline</button></div>`)}</div>`
          : html`<p class="mu">No requests waiting for you.</p>`}
          <h3 class="mt">Sent</h3>${d.outgoing.length
          ? html`<div class="list">${d.outgoing.map((r) => html`<div class="li">${av(r.name, 'sm')}<span class="f1"><b>${r.name}</b><br><span class="xs mu">Sent ${ago(r.at)}</span></span><button type="button" class="btn ghost sm" data-act="net.withdraw" data-id="${r.id}">Withdraw</button></div>`)}</div>`
          : html`<p class="mu">You have no pending requests.</p>`}`;
      } else if (ui.tab === 'connections') {
        body = d.connections.length
          ? html`<div class="grid g3">${d.connections.map((c) => html`<div class="card"><div class="row nw">${av(c.name)}<div class="f1"><h4 style="margin:0">${c.name}</h4><div class="xs mu">${ROLE_LABEL[c.role]}</div></div></div>
              ${c.conversation_id ? html`<a class="btn sm mt-s" href="/${role}/messages?c=${c.conversation_id}">${ic('chat')} Message</a>` : ''}</div>`)}</div>`
          : empty('users', 'No connections yet', 'Send a few requests from Discover.');
      } else {
        body = d.intros.length
          ? html`<div class="list">${d.intros.map((i) => html`<div class="li"><span class="f1"><b>${i.startup}</b> would like to meet you<br><span class="sm mu">${i.note || ''}</span></span>
              ${i.status === 'forwarded' ? html`<button type="button" class="btn sm" data-act="net.intro" data-id="${i.id}" data-accept="true">Accept</button><button type="button" class="btn ghost sm" data-act="net.intro" data-id="${i.id}" data-accept="false">Decline</button>`
                : i.conversation_id ? html`<a class="btn soft sm" href="/${role}/messages?c=${i.conversation_id}">Message</a>` : tag('Accepted', 'ok')}</div>`)}</div>`
          : empty('rocket', 'No introductions', 'If you are open to co-founder introductions, Vignova may connect you with a founder. Turn it on in your profile.');
      }
      return html`${pageHead('Your network', 'Connect first, then message. Requests are limited to keep the network serious.', tag(`${d.quota.left} of ${d.quota.limit} requests left this week`, d.quota.left ? '' : 'wn'))}
        ${seg(tabs, ui.tab, 'net.tab', 'Network sections')}<div class="mt">${body}</div>`;
    },
  };
}

const search = debounce(async () => {
  const view = currentView();
  if (!view) return;
  const result = await fetchPeople(view.ctx, view.ui);
  update((ui, d) => { d.people = result.people; d.quota = result.quota; });
}, 300);

register({
  'net.tab': (el) => update((ui) => { ui.tab = el.dataset.value; }),
  'net.search': (el) => {
    currentView().ui.q = el.value;
    search();
  },
  'net.role': async (el) => {
    const view = currentView();
    view.ui.role = el.dataset.value;
    const result = await fetchPeople(view.ctx, view.ui);
    update((ui, d) => { d.people = result.people; d.quota = result.quota; });
  },
  'net.connect': async (el) => {
    await api.post('/network/requests', { body: { user_id: Number(el.dataset.id) } });
    toast(`Connection request sent to ${el.dataset.name}`, 'send');
    await reload();
  },
  'net.respond': async (el) => {
    await api.post(`/network/requests/${el.dataset.id}/respond`, { body: { accept: el.dataset.accept === 'true' } });
    toast(el.dataset.accept === 'true' ? 'Connected' : 'Request declined', 'users');
    await reload();
  },
  'net.withdraw': async (el) => {
    await api.post(`/network/requests/${el.dataset.id}/withdraw`);
    toast('Request withdrawn', 'x');
    await reload();
  },
  'net.intro': async (el) => {
    await api.post(`/me/intros/${el.dataset.id}/respond`, { body: { accept: el.dataset.accept === 'true' } });
    toast(el.dataset.accept === 'true' ? 'Introduction accepted. A chat is open.' : 'Introduction declined');
    await reload();
  },
});

export const routes = ['student', 'professional', 'startup'].map(networkRoute);
